import { makeStyles } from '@material-ui/core/styles';
import { observer } from 'mobx-react';
import { useStatusDashboardStore } from '../hooks/useStatusDashboardStore';
import { BRAND_OPTIONS, Channel, RANGE_OPTIONS, Role } from '../utils/statusDashboardConstants';
import { PillMultiSelect } from './pillMultiSelect';
import { PillSelect } from './pillSelect';
import { PillToggle } from './pillToggle';
import { DASHBOARD_COLORS } from './statusDashboardStyles';

const useStyles = makeStyles(() => ({
  root: {
    display: 'flex',
    alignItems: 'flex-end',
    justifyContent: 'space-between',
    gap: 24,
    flexWrap: 'wrap',
  },
  titleBlock: {
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
  },
  breadcrumb: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    fontSize: 11,
    letterSpacing: '.04em',
    textTransform: 'uppercase',
    color: DASHBOARD_COLORS.muted,
  },
  title: {
    margin: 0,
    fontSize: 24,
    fontWeight: 700,
    lineHeight: 1.2,
  },
  controls: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    flexWrap: 'wrap',
  },
}));

export const StatusDashboardHeader = observer(() => {
  const classes = useStyles();
  const { statusDashboardStore: store } = useStatusDashboardStore();

  const crumb = store.view === 'overview' ? 'Bulk sends' : store.view === 'campaign' ? 'Campaign' : 'Recipient';
  const title =
    store.view === 'overview'
      ? 'Bulk send performance'
      : store.view === 'campaign'
        ? store.selectedCampaign.name
        : store.selectedRecipient.name;

  return (
    <div className={classes.root}>
      <div className={classes.titleBlock}>
        <div className={classes.breadcrumb}>
          <span>Outreach</span>
          <span>/</span>
          <span>{crumb}</span>
        </div>
        <h1 className={classes.title}>{title}</h1>
      </div>
      <div className={classes.controls}>
        <PillToggle<Role>
          value={store.role}
          onChange={role => store.setRole(role)}
          options={[
            { value: 'marketing', label: 'Marketing blasts' },
            { value: 'sales', label: 'My outreach' },
          ]}
        />
        <PillToggle<Channel>
          value={store.channel}
          onChange={channel => store.setChannel(channel)}
          options={[
            { value: 'email', label: 'Email' },
            {
              value: 'sms',
              label: 'SMS',
              // SMS bulk sends are not tracked yet, so the dashboard is email only for now
              disabled: true,
              tooltip: 'SMS coming soon',
            },
          ]}
        />
        <PillSelect
          label="Range"
          value={store.range}
          options={RANGE_OPTIONS}
          onChange={range => store.setRange(range)}
        />
        {store.canChooseSender && store.originatorOptions.length > 0 && (
          <PillMultiSelect
            label={store.senderLabel}
            allLabel={store.allSendersLabel}
            value={store.senders}
            options={store.senderOptions}
            onChange={senders => store.setSenders(senders)}
          />
        )}
        {store.isMarketing && (
          <PillSelect
            label="Brand"
            value={store.brand}
            options={BRAND_OPTIONS}
            onChange={brand => store.setBrand(brand)}
          />
        )}
      </div>
    </div>
  );
});
